class ScrollingImageBanner extends HTMLElement {
  constructor() {
    super();
    this.onResize = this.debounce(this.setupAnimation.bind(this), 100);
  }

  connectedCallback() {
    this.viewport = this.querySelector('.scrolling-banner__viewport');
    this.track = this.querySelector('.scrolling-banner__track');

    if (!this.viewport || !this.track) return;

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.waitForImages();
    window.addEventListener('resize', this.onResize);

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(this.onResize);
      this.resizeObserver.observe(this.viewport);
    }
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.onResize);
    this.resizeObserver?.disconnect();
    this.cancelAnimation();
  }

  debounce(fn, delay) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn(...args), delay);
    };
  }

  waitForImages() {
    const images = [...this.track.querySelectorAll('img')];
    const imageReadyPromises = images.map((img) => {
      if (img.complete && img.naturalWidth > 0) {
        return typeof img.decode === 'function' ? img.decode().catch(() => undefined) : Promise.resolve();
      }

      return new Promise((resolve) => {
        img.addEventListener('load', () => resolve(), { once: true });
        img.addEventListener('error', () => resolve(), { once: true });
      });
    });

    Promise.all(imageReadyPromises).then(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(this.setupAnimation);
      });
    });
  }

  cancelAnimation() {
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }

    if (this.animation) {
      this.animation.cancel();
      this.animation = null;
    }
  }

  syncSlideWidths(slides, setSize) {
    const widths = [];

    for (let i = 0; i < setSize; i += 1) {
      const img = slides[i].querySelector('img');
      const width = Math.round(img?.getBoundingClientRect().width || slides[i].getBoundingClientRect().width);
      widths.push(width);

      slides[i].style.width = `${width}px`;
      slides[i + setSize].style.width = `${width}px`;
    }

    return widths.reduce((total, width) => total + width, 0);
  }

  getLoopWidth(slides, setSize) {
    const duplicateStart = slides[setSize];
    if (duplicateStart) {
      return Math.round(duplicateStart.offsetLeft);
    }

    return this.syncSlideWidths(slides, setSize);
  }

  startScrollLoop(loopWidth, duration) {
    const reverse =
      this.dataset.scrollDirection === 'right' || this.dataset.scrollDirection === 'up';
    const pixelsPerMs = loopWidth / duration;
    let offset = 0;
    let lastTimestamp = null;

    const tick = (timestamp) => {
      if (lastTimestamp !== null) {
        const delta = timestamp - lastTimestamp;
        offset += (reverse ? -1 : 1) * pixelsPerMs * delta;

        if (offset >= loopWidth) {
          offset -= loopWidth;
        } else if (offset < 0) {
          offset += loopWidth;
        }
      }

      lastTimestamp = timestamp;
      this.track.style.transform = `translate3d(${-offset}px, 0, 0)`;
      this.rafId = requestAnimationFrame(tick);
    };

    this.rafId = requestAnimationFrame(tick);
  }

  setupAnimation() {
    if (!this.viewport || !this.track) return;

    this.cancelAnimation();

    const slides = [...this.track.querySelectorAll('.scrolling-banner__slide')];
    if (!slides.length) {
      this.track.classList.remove('scrolling-banner__track--static');
      this.track.style.transform = '';
      return;
    }

    const viewportWidth = this.viewport.clientWidth;
    const viewportHeight = this.viewport.clientHeight;
    const setSize = slides.length / 2;

    slides.forEach((slide) => {
      slide.style.width = '';
    });

    this.track.classList.remove('scrolling-banner__track--static', 'scrolling-banner__track--animate');
    this.track.style.removeProperty('--scroll-distance');
    this.track.style.removeProperty('--scroll-duration');
    this.track.style.transform = '';

    if (this.reducedMotion || viewportWidth <= 0 || viewportHeight <= 0) {
      this.track.classList.add('scrolling-banner__track--static');
      return;
    }

    this.syncSlideWidths(slides, setSize);
    const loopWidth = this.getLoopWidth(slides, setSize);

    if (loopWidth <= 0) {
      this.track.classList.add('scrolling-banner__track--static');
      return;
    }

    const duration = (parseFloat(this.dataset.scrollDuration) || 20) * 1000;

    this.track.classList.remove('scrolling-banner__track--static');
    this.startScrollLoop(loopWidth, duration);
  }
}

customElements.define('scrolling-image-banner', ScrollingImageBanner);
